import { useEffect, useRef } from "react";
import styled from "styled-components";
import { CContainer, CHeader, CHeaderNav, CHeaderToggler } from "@coreui/react";
import AppHeaderDropdown from "./AppHeaderDropdown";
import { useNav } from "../../context/NavContext";
import {
  getForm,
  itemExists,
  stringifyName,
  useGetUserPermissions,
} from "../../utils/helpers";
import {
  MenuOpenRounded,
  DescriptionOutlined,
  FilterListOutlined,
} from "@mui/icons-material";
import {
  NotificationsOutline,
  SettingsOutline,
} from "@raresail/react-ionicons";
import { IconButton } from "@mui/material";
import {
  APP_NAME,
  CAN_ADD_APPLICATION,
  CAN_ADMIN,
  CAN_PUBLIC,
  LOAN_APPLICATION,
} from "../../utils/constants";
import { Link, useNavigate } from "react-router-dom";
import { URL_SETTINGS } from "../../utils/urls";
import { useGlobalContext } from "../../context/GlobalContext";
import { blueColor } from "../styled/Theme";

const AppHeader = ({ profile }) => {
  const permissions = useGetUserPermissions();
  const { updateData, data: globalData } = useGlobalContext();
  const { pageHeader } = globalData || {};
  const navigate = useNavigate();
  const isAdmin = itemExists(permissions, [CAN_ADMIN]);

  const handleOpenGenerateStatement = () => {
    updateData({ showGenerateStatement: true });
    navigate("/");
  };

  const headerRef = useRef<HTMLDivElement>();
  const { sidebarShow, setSidebarShow } = useNav();

  useEffect(() => {
    if (profile) {
      window.document.title = `${APP_NAME}: ${stringifyName(profile)}`;
    }
  }, [profile]);

  useEffect(() => {
    document.addEventListener("scroll", () => {
      headerRef.current &&
        headerRef.current?.classList.toggle(
          "shadow-sm",
          document.documentElement.scrollTop > 0,
        );
    });
  }, []);

  return (
    <CHeader
      style={{ zIndex: 100 }}
      position="sticky"
      className="mb-4 p-0"
      ref={headerRef as any}
    >
      <CContainer className="border-bottom px-4" fluid>
        <CHeaderToggler
          onClick={() => setSidebarShow(!sidebarShow)}
          style={{ marginInlineStart: "-14px" }}
        >
          <MenuOpenRounded />
        </CHeaderToggler>

        {pageHeader?.title && (
          <HeaderPageInfo>
            <HeaderPageTitle>{pageHeader.title}</HeaderPageTitle>
            <HeaderPageDate>{pageHeader.subtitle}</HeaderPageDate>
          </HeaderPageInfo>
        )}

        <CHeaderNav className="ms-auto gap10" />
        <div className="flexNullCenter gap10">
          {pageHeader?.systemOnline && (
            <HeaderStatusPill>
              <HeaderStatusDot /> System Online
            </HeaderStatusPill>
          )}
          {isAdmin && (pageHeader?.pendingApprovals ?? 0) > 0 && (
            <HeaderPendingPill>
              {pageHeader!.pendingApprovals} Pending Approvals
            </HeaderPendingPill>
          )}
          {pageHeader?.onFilter && (
            <HeaderFilterBtn onClick={pageHeader.onFilter}>
              <FilterListOutlined style={{ fontSize: 14 }} />
              Filter
            </HeaderFilterBtn>
          )}
          {itemExists(permissions, [CAN_PUBLIC, CAN_ADD_APPLICATION]) && (
            <Link to={getForm(LOAN_APPLICATION)}>
              {/* <Button
                className="font12"
                type="primary"
                icon={<Add fontSize={15} />}
                style={{ borderRadius: 20 }}
              > */}
              {/* Make Deposit */}
              {/* </Button> */}
            </Link>
          )}
          <GenStatementBtn onClick={handleOpenGenerateStatement}>
            <DescriptionOutlined style={{ fontSize: 15 }} />
            Generate Statement
          </GenStatementBtn>
          <IconButton size="small" sx={{ bgcolor: "#eee" }}>
            <NotificationsOutline />
          </IconButton>
          {/* {itemExists(permissions, [CAN_ADMIN]) && ( */}
          <Link to={URL_SETTINGS}>
            <IconButton size="small" sx={{ bgcolor: "#eee" }}>
              <SettingsOutline />
            </IconButton>
          </Link>
          {/* )} */}

          <CHeaderNav>
            <AppHeaderDropdown />
          </CHeaderNav>
        </div>
      </CContainer>
    </CHeader>
  );
};

export default AppHeader;

const HeaderPageInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 10px;
`;

const HeaderPageTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
`;

const HeaderPageDate = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
`;

const HeaderStatusPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  font-size: 12px;
  font-weight: 600;
  color: #16a34a;
  white-space: nowrap;
`;

const HeaderStatusDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 2px #22c55e33;
  flex-shrink: 0;
`;

const HeaderPendingPill = styled.div`
  padding: 4px 10px;
  border-radius: 20px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  font-size: 12px;
  font-weight: 600;
  color: #ea580c;
  white-space: nowrap;
`;

const HeaderFilterBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #374151;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const GenStatementBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${blueColor}38;
  background: ${blueColor}0d;
  color: ${blueColor};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.1px;
  white-space: nowrap;
  transition:
    background 0.18s,
    border-color 0.18s,
    box-shadow 0.18s;

  &:hover {
    background: ${blueColor}1c;
    border-color: ${blueColor}60;
    box-shadow: 0 2px 10px ${blueColor}22;
  }

  &:active {
    background: ${blueColor}28;
  }
`;
